import React, { useState, useEffect } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  TextField,
  Box,
  Stepper,
  Step,
  StepLabel,
  Typography,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  IconButton,
  FormHelperText,
  Chip,
  Stack,
  Divider,
  Alert
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import DeleteIcon from '@mui/icons-material/Delete';
import SwapVertIcon from '@mui/icons-material/SwapVert';
import * as api from '../services/api';

const SequenceForm = ({ 
  open, 
  onClose, 
  onSave, 
  editSequence = null 
}) => {
  const [activeStep, setActiveStep] = useState(0);
  const [fileTypes, setFileTypes] = useState([]);
  const [operations, setOperations] = useState([]);
  const [textProcessingOperations, setTextProcessingOperations] = useState([]);
  const [validationError, setValidationError] = useState('');

  // Formulario de secuencia
  const [sequence, setSequence] = useState({
    name: '',
    description: '',
    steps: []
  });

  // Estado para el paso actual que se está editando
  const [currentStep, setCurrentStep] = useState({
    type: '',
    inputType: '',
    outputType: '',
    operation: '',
    language: ''
  });

  // Función para resetear el formulario
  const resetForm = () => {
    setSequence({
      name: '',
      description: '',
      steps: []
    });
    
    setCurrentStep({
      type: '',
      inputType: '',
      outputType: '',
      operation: '',
      language: ''
    });
    
    setActiveStep(0);
    setValidationError('');
  };

  // Función para manejar el cierre del diálogo
  const handleClose = () => {
    resetForm();
    onClose();
  };

  useEffect(() => {
    // Si se está editando una secuencia existente, cargar sus datos
    if (editSequence) {
      setSequence({
        ...editSequence
      });
    } else {
      setSequence({
        name: '',
        description: '',
        steps: []
      });
    }

    // Cargar tipos de archivo y operaciones disponibles
    fetchFileTypes();
    fetchOperations();
    fetchTextOperations();
  }, [editSequence]);

  const fetchFileTypes = async () => {
    try {
      const types = await api.fetchFileTypes();
      setFileTypes(types);
    } catch (error) {
      console.error('Error al cargar tipos de archivo:', error);
    }
  };

  const fetchOperations = async () => {
    try {
      const ops = await api.fetchSequenceOperations();
      setOperations(ops);
    } catch (error) {
      console.error('Error al cargar operaciones:', error);
    }
  };

  const fetchTextOperations = async () => {
    try {
      const ops = await api.fetchOperations();
      // Guardar las operaciones completas en lugar de solo las claves
      setTextProcessingOperations(ops);
    } catch (error) {
      console.error('Error al cargar operaciones de texto:', error);
    }
  };

  const handleNext = () => {
    if (activeStep === 0) {
      // Validar información general
      if (!sequence.name.trim()) {
        setValidationError('El nombre de la secuencia es obligatorio');
        return;
      }
      setValidationError('');
    }
    setActiveStep((prevActiveStep) => prevActiveStep + 1);
  };

  const handleBack = () => {
    setActiveStep((prevActiveStep) => prevActiveStep - 1);
    setValidationError('');
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setSequence({
      ...sequence,
      [name]: value
    });
  };

  const handleStepTypeChange = (e) => {
    const selectedType = e.target.value;
    const selectedOperation = operations.find(op => op.type === selectedType);
    
    setCurrentStep({
      type: selectedType,
      inputType: selectedOperation ? selectedOperation.inputType : '',
      outputType: selectedOperation ? selectedOperation.outputType : '',
      operation: '',
      language: ''
    });
  };

  const handleStepOperationChange = (e) => {
    setCurrentStep({
      ...currentStep,
      operation: e.target.value
    });
  };

  const handleStepLanguageChange = (e) => {
    setCurrentStep({
      ...currentStep,
      language: e.target.value
    });
  };

  const addStep = () => {
    // Validar que se haya seleccionado un tipo de paso
    if (!currentStep.type) {
      setValidationError('Debes seleccionar un tipo de operación');
      return;
    }

    // Validar requisitos adicionales según el tipo de operación
    const operation = operations.find(op => op.type === currentStep.type);
    if (operation?.requiresOperation && !currentStep.operation) {
      setValidationError('Esta operación requiere seleccionar una operación de procesamiento');
      return;
    }
    
    if (operation?.requiresLanguage && !currentStep.language) {
      setValidationError('Esta operación requiere seleccionar un idioma');
      return;
    }

    // Validar compatibilidad con el paso anterior
    if (sequence.steps.length > 0) {
      const lastStep = sequence.steps[sequence.steps.length - 1];
      if (lastStep.outputType !== currentStep.inputType) {
        setValidationError(`Incompatibilidad: La salida del paso anterior (${lastStep.outputType}) no coincide con la entrada de este paso (${currentStep.inputType})`);
        return;
      }
    }

    setValidationError('');
    
    // Añadir el paso a la secuencia
    setSequence({
      ...sequence,
      steps: [...sequence.steps, { ...currentStep }]
    });
    
    // Limpiar el formulario del paso actual
    setCurrentStep({
      type: '',
      inputType: '',
      outputType: '',
      operation: '',
      language: ''
    });
  };

  const removeStep = (index) => {
    const newSteps = [...sequence.steps];
    newSteps.splice(index, 1);
    setSequence({
      ...sequence,
      steps: newSteps
    });
  };

  const moveStep = (index, direction) => {
    if ((direction === -1 && index === 0) || 
        (direction === 1 && index === sequence.steps.length - 1)) {
      return;
    }
    
    const newSteps = [...sequence.steps];
    const step = newSteps[index];
    newSteps.splice(index, 1);
    newSteps.splice(index + direction, 0, step);
    
    // Validar compatibilidad después de mover
    for (let i = 1; i < newSteps.length; i++) {
      if (newSteps[i-1].outputType !== newSteps[i].inputType) {
        setValidationError(`Movimiento inválido: Crearía incompatibilidad entre pasos ${i} y ${i+1}`);
        return;
      }
    }
    
    setValidationError('');
    setSequence({
      ...sequence,
      steps: newSteps
    });
  };

  const handleSave = () => {
    // Validar que haya al menos un paso
    if (sequence.steps.length === 0) {
      setValidationError('La secuencia debe tener al menos un paso');
      return;
    }
    
    setValidationError('');
    onSave(sequence);
    resetForm();
    onClose();
  };

  // Función auxiliar para obtener el nombre de visualización de una operación
  const getOperationDisplayName = (operationKey) => {
    if (!operationKey) return '';
    
    // Si es una operación de texto, buscar en textProcessingOperations por su name
    if (textProcessingOperations && textProcessingOperations[operationKey]) {
      return textProcessingOperations[operationKey].name || operationKey;
    }
    
    return operationKey;
  };

  return (
    <Dialog 
      open={open} 
      onClose={handleClose}
      fullWidth
      maxWidth="md"
    >
      <DialogTitle>
        {editSequence ? 'Editar Secuencia' : 'Crear Nueva Secuencia'}
      </DialogTitle>
      
      <DialogContent>
        <Stepper activeStep={activeStep} sx={{ pt: 3, pb: 3 }}>
          <Step>
            <StepLabel>Información General</StepLabel>
          </Step>
          <Step>
            <StepLabel>Definir Pasos</StepLabel>
          </Step>
          <Step>
            <StepLabel>Revisar y Guardar</StepLabel>
          </Step>
        </Stepper>
        
        {validationError && (
          <Alert severity="error" sx={{ mt: 2, mb: 2 }}>
            {validationError}
          </Alert>
        )}
        
        {activeStep === 0 && (
          <Box sx={{ mt: 2 }}>
            <TextField
              name="name"
              label="Nombre de la secuencia"
              fullWidth
              margin="normal"
              value={sequence.name}
              onChange={handleInputChange}
              error={validationError.includes('nombre')}
              required
            />
            
            <TextField
              name="description"
              label="Descripción"
              fullWidth
              margin="normal"
              value={sequence.description}
              onChange={handleInputChange}
              multiline
              rows={4}
            />
          </Box>
        )}
        
        {activeStep === 1 && (
          <Box sx={{ mt: 2 }}>
            <Typography variant="h6" gutterBottom>
              Pasos de la secuencia
            </Typography>
            
            {sequence.steps.length > 0 && (
              <Box sx={{ mb: 3 }}>
                <Typography variant="subtitle1" gutterBottom>
                  Pasos actuales:
                </Typography>
                
                {sequence.steps.map((step, index) => (
                  <Box 
                    key={index} 
                    sx={{ 
                      display: 'flex', 
                      alignItems: 'center', 
                      mb: 1,
                      p: 1,
                      border: '1px solid #e0e0e0',
                      borderRadius: 1
                    }}
                  >
                    <Typography variant="body2" sx={{ mr: 1, minWidth: '20px' }}>
                      {index + 1}.
                    </Typography>
                    
                    <Stack direction="row" spacing={1} sx={{ flexGrow: 1 }}>
                      <Chip 
                        label={step.type} 
                        color="primary" 
                        size="small" 
                      />
                      <Chip 
                        label={`${step.inputType} → ${step.outputType}`} 
                        size="small" 
                        variant="outlined" 
                      />
                      {step.operation && (
                        <Chip 
                          label={`Operación: ${getOperationDisplayName(step.operation)}`} 
                          size="small" 
                        />
                      )}
                      {step.language && (
                        <Chip 
                          label={`Idioma: ${step.language}`} 
                          size="small" 
                        />
                      )}
                    </Stack>
                    
                    <IconButton 
                      size="small" 
                      onClick={() => moveStep(index, -1)}
                      disabled={index === 0}
                    >
                      <SwapVertIcon fontSize="small" />
                    </IconButton>
                    
                    <IconButton 
                      size="small" 
                      onClick={() => moveStep(index, 1)}
                      disabled={index === sequence.steps.length - 1}
                    >
                      <SwapVertIcon fontSize="small" sx={{ transform: 'rotate(180deg)' }} />
                    </IconButton>
                    
                    <IconButton 
                      size="small" 
                      color="error" 
                      onClick={() => removeStep(index)}
                    >
                      <DeleteIcon fontSize="small" />
                    </IconButton>
                  </Box>
                ))}
              </Box>
            )}
            
            <Divider sx={{ mb: 3 }} />
            
            <Typography variant="subtitle1" gutterBottom>
              Agregar nuevo paso:
            </Typography>
            
            <Box sx={{ mb: 2 }}>
              <FormControl fullWidth margin="normal">
                <InputLabel id="step-type-label">Tipo de operación</InputLabel>
                <Select
                  labelId="step-type-label"
                  value={currentStep.type}
                  onChange={handleStepTypeChange}
                  label="Tipo de operación"
                >
                  <MenuItem value="">
                    <em>Selecciona una operación</em>
                  </MenuItem>
                  {operations.map((op) => (
                    <MenuItem key={op.type} value={op.type}>
                      {op.description} ({op.inputType} → {op.outputType})
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
              
              {currentStep.type === 'process_text' && (
                <FormControl fullWidth margin="normal" required error={validationError.includes('operación de procesamiento')}>
                  <InputLabel id="step-operation-label">Operación de Procesamiento</InputLabel>
                  <Select
                    labelId="step-operation-label"
                    value={currentStep.operation}
                    label="Operación de Procesamiento"
                    onChange={handleStepOperationChange}
                  >
                    {textProcessingOperations.map((op) => (
                      <MenuItem key={op.name} value={op.name}>
                        {op.name}
                      </MenuItem>
                    ))}
                  </Select>
                  {validationError.includes('operación de procesamiento') && <FormHelperText>{validationError}</FormHelperText>}
                </FormControl>
              )}
              
              {currentStep.type === 'text2voice' && (
                <FormControl fullWidth margin="normal">
                  <InputLabel id="language-label">Idioma</InputLabel>
                  <Select
                    labelId="language-label"
                    value={currentStep.language}
                    onChange={handleStepLanguageChange}
                    label="Idioma"
                  >
                    <MenuItem value="">
                      <em>Selecciona un idioma</em>
                    </MenuItem>
                    <MenuItem value="es">Español</MenuItem>
                    <MenuItem value="en">Inglés</MenuItem>
                  </Select>
                </FormControl>
              )}
              
              <Box sx={{ mt: 2 }}>
                <Button
                  variant="outlined"
                  startIcon={<AddIcon />}
                  onClick={addStep}
                  disabled={!currentStep.type}
                >
                  Añadir Paso
                </Button>
              </Box>
            </Box>
          </Box>
        )}
        
        {activeStep === 2 && (
          <Box sx={{ mt: 2 }}>
            <Typography variant="h6" gutterBottom>
              Resumen de la secuencia
            </Typography>
            
            <Box sx={{ mb: 2 }}>
              <Typography variant="subtitle1">
                {sequence.name}
              </Typography>
              
              <Typography variant="body2" color="text.secondary">
                {sequence.description || 'Sin descripción'}
              </Typography>
            </Box>
            
            <Typography variant="subtitle1" gutterBottom>
              Pasos ({sequence.steps.length}):
            </Typography>
            
            {sequence.steps.map((step, index) => (
              <Box 
                key={index} 
                sx={{ 
                  p: 1, 
                  mb: 1, 
                  border: '1px solid #e0e0e0',
                  borderRadius: 1
                }}
              >
                <Typography variant="body1">
                  {index + 1}. {step.type}
                </Typography>
                
                <Box sx={{ ml: 3 }}>
                  <Typography variant="body2">
                    Entrada: {step.inputType} → Salida: {step.outputType}
                  </Typography>
                  
                  {step.operation && (
                    <Typography variant="body2">
                      Operación: {getOperationDisplayName(step.operation)}
                    </Typography>
                  )}
                  
                  {step.language && (
                    <Typography variant="body2">
                      Idioma: {step.language}
                    </Typography>
                  )}
                </Box>
              </Box>
            ))}
          </Box>
        )}
      </DialogContent>
      
      <DialogActions>
        <Button onClick={handleClose}>Cancelar</Button>
        
        {activeStep > 0 && (
          <Button onClick={handleBack}>
            Atrás
          </Button>
        )}
        
        {activeStep < 2 ? (
          <Button onClick={handleNext}>
            Siguiente
          </Button>
        ) : (
          <Button 
            onClick={handleSave}
            color="primary"
            variant="contained"
            disabled={sequence.steps.length === 0}
          >
            Guardar Secuencia
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default SequenceForm; 